/**
 * CartDrawer + WishlistDrawer – Side-Sheets für den Shop.
 * Zeigt Items, Preise, Mengensteuerung und CTAs zu Checkout/Angebot.
 */
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Plus, Minus, ShoppingBag, Trash2, ArrowRight, Heart, FileText, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useCart, formatEUR } from './CartContext';
import HCaptchaModal from './HCaptchaModal';
import CartCrossSell from './CartCrossSell';
import { postNotifyWaitlist } from '@/lib/shopNotify';
import { EXCHANGE_NOTICES } from '@/lib/exchangeNotices';

const cartNoticeLabel = (it) =>
  EXCHANGE_NOTICES.find((n) => n.key === it.exchange_notice)?.badge || '★ Sonderanfertigung';

function Overlay({ open, onClose }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[100]"
          data-testid="drawer-overlay"
        />
      )}
    </AnimatePresence>
  );
}

function DrawerShell({ open, onClose, title, testId, children, footer }) {
  return (
    <>
      <Overlay open={open} onClose={onClose} />
      <AnimatePresence>
        {open && (
          <motion.aside
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.35, ease: [0.22, 0.61, 0.36, 1] }}
            className="fixed top-0 right-0 h-full w-full sm:w-[440px] bg-gradient-to-b from-[#f6efdf] to-[#f0e6c9] shadow-2xl z-[101] flex flex-col"
            data-testid={testId}
          >
            <header className="flex items-center justify-between px-6 py-5 border-b border-[#C9A23C]/25">
              <h2 className="hs-serif text-xl text-[#171512]">{title}</h2>
              <button
                type="button"
                onClick={onClose}
                className="w-9 h-9 rounded-full hover:bg-white/60 flex items-center justify-center"
                aria-label="Schließen"
                data-testid={`${testId}-close`}
              >
                <X className="w-5 h-5" />
              </button>
            </header>
            <div className="flex-1 overflow-y-auto px-6 py-4">{children}</div>
            {footer && <div className="px-6 py-5 border-t border-[#C9A23C]/25 bg-white/50 backdrop-blur">{footer}</div>}
          </motion.aside>
        )}
      </AnimatePresence>
    </>
  );
}

// ------------------------------------------------------------------
// CART DRAWER
// ------------------------------------------------------------------
export function CartDrawer() {
  const {
    cart, cartOpen, setCartOpen, totalCents, subtotalCents, discountCents,
    vipDiscountCents, vipPercent,
    netCents, taxCents, taxBreakdown, couponInfo,
    applyCoupon, removeCoupon, updateCartItem, removeCartItem, cartCount,
  } = useCart();
  const navigate = useNavigate();
  const items = cart?.items || [];
  const [couponInput, setCouponInput] = React.useState('');
  const [applying, setApplying] = React.useState(false);
  // Warenkorb-Validierung: prüft bei jedem Öffnen des Drawers alle Positionen
  // auf aktuellen Bestand. Falls Positionen ausverkauft sind → Toast + Notify-
  // Popup mit E-Mail-Feld. Verhindert, dass Kunden zum Checkout gelangen mit
  // OOS-Artikeln im Cart.
  const [oosIssues, setOosIssues] = React.useState([]); // [{product_id, product_name, product_slug, variant, requested, available}]
  const [notifyOpen, setNotifyOpen] = React.useState(false);
  const [notifyEmail, setNotifyEmail] = React.useState('');
  const [notifyBusy, setNotifyBusy] = React.useState(false);
  const [notifiedIds, setNotifiedIds] = React.useState(new Set());
  // Anti-Spam: Honeypot + Render-Timestamp (Bots senden im Millisekunden-Bereich).
  const [notifyFormTs] = React.useState(() => Date.now());
  const [notifyHoney, setNotifyHoney] = React.useState('');
  // hCaptcha-Fallback bei weichem Rate-Limit-Cutoff
  const [captchaOpen, setCaptchaOpen] = React.useState(false);
  const [captchaMsg, setCaptchaMsg] = React.useState('');
  // Merken uns die zuletzt versuchte OOS-Position, damit wir sie nach dem
  // Captcha-Solve mit dem Token erneut submitten können.
  const [pendingItem, setPendingItem] = React.useState(null);

  React.useEffect(() => {
    if (!cartOpen || items.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const sid = (() => {
          try { return localStorage.getItem('hs_session') || ''; } catch { return ''; }
        })();
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/shop/cart/validate`, {
          headers: sid ? { 'x-hs-session': sid } : {},
          credentials: 'include',
        });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (data.oos_items && data.oos_items.length > 0) {
          setOosIssues(data.oos_items);
          setNotifyOpen(true);
          toast.error(
            `${data.oos_items.length} Artikel im Warenkorb ${data.oos_items.length === 1 ? 'ist' : 'sind'} ausverkauft.`,
            { duration: 6000 },
          );
        } else {
          setOosIssues([]);
        }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [cartOpen, items.length]);

  const submitNotify = async (item, captchaToken = null) => {
    if (!notifyEmail || !/^\S+@\S+\.\S+$/.test(notifyEmail)) {
      toast.error('Bitte gültige E-Mail eintragen');
      return;
    }
    setNotifyBusy(true);
    try {
      const result = await postNotifyWaitlist({
        productId: item.product_id,
        email: notifyEmail,
        variant: item.variant || {},
        honey: notifyHoney,
        formTs: notifyFormTs,
        captchaToken,
      });
      if (result.ok) {
        setNotifiedIds((prev) => new Set(prev).add(`${item.product_id}|${item.variant?.color || ''}|${item.variant?.size || ''}`));
        setCaptchaOpen(false);
        setPendingItem(null);
        toast.success(
          result.data?.verified
            ? 'Wir melden uns per E-Mail'
            : 'Fast fertig — bitte E-Mail-Bestätigung anklicken',
        );
      } else if (result.status === 'captcha_required') {
        setPendingItem(item);
        setCaptchaMsg(result.detail || '');
        setCaptchaOpen(true);
      } else {
        toast.error(result.detail);
      }
    } finally { setNotifyBusy(false); }
  };

  const goCheckout = () => {
    setCartOpen(false);
    navigate('/checkout');
  };

  const doApplyCoupon = async () => {
    if (!couponInput.trim()) return;
    setApplying(true);
    try {
      await applyCoupon(couponInput.trim());
      setCouponInput('');
    } catch (_) { /* toast already shown */ }
    finally { setApplying(false); }
  };

  return (
    <>
    <DrawerShell
      open={cartOpen}
      onClose={() => setCartOpen(false)}
      title={`Warenkorb (${cartCount})`}
      testId="cart-drawer"
      footer={
        items.length > 0 && (
          <>
            {/* Gutschein-Sektion */}
            <div className="mb-4" data-testid="cart-coupon-section">
              {couponInfo ? (
                <div className="flex items-center justify-between px-3 py-2 rounded-md bg-[#fff8e1] border border-[#C9A23C]/40" data-testid="cart-coupon-active">
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-[#8a6d10] font-bold">Aktiver Gutschein</div>
                    <div className="text-sm font-semibold text-[#171512] truncate">
                      {couponInfo.code} · {couponInfo.type === 'percent' ? `${couponInfo.value} %` : formatEUR(couponInfo.value)} Rabatt
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={removeCoupon}
                    className="text-xs text-red-700 hover:underline font-semibold ml-2"
                    data-testid="cart-coupon-remove"
                  >
                    Entfernen
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={couponInput}
                    onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                    placeholder="Gutschein-Code"
                    onKeyDown={(e) => e.key === 'Enter' && doApplyCoupon()}
                    className="flex-1 px-3 py-2 rounded-md border border-[#C9A23C]/40 text-sm bg-white/70 uppercase tracking-wider focus:outline-none focus:border-[#C9A23C]"
                    data-testid="cart-coupon-input"
                  />
                  <button
                    type="button"
                    onClick={doApplyCoupon}
                    disabled={applying || !couponInput.trim()}
                    className="px-3 py-2 rounded-md bg-[#171512] text-[#E8D291] text-xs font-bold disabled:opacity-40"
                    data-testid="cart-coupon-apply"
                  >
                    {applying ? '…' : 'Einlösen'}
                  </button>
                </div>
              )}
            </div>
            {/* Summenblock mit MwSt-Aufschlüsselung */}
            <div className="space-y-1 mb-3 text-sm">
              <div className="flex items-center justify-between text-[#3a2f1e]/70">
                <span>Zwischensumme</span>
                <span data-testid="cart-subtotal">{formatEUR(subtotalCents)}</span>
              </div>
              {discountCents > 0 && (
                <div className="flex items-center justify-between text-[#8a6d10]">
                  <span>Rabatt {couponInfo ? `(${couponInfo.code})` : ''}</span>
                  <span data-testid="cart-discount">− {formatEUR(discountCents)}</span>
                </div>
              )}
              {vipDiscountCents > 0 && (
                <div className="flex items-center justify-between text-[#8a6d10]" data-testid="cart-vip-row">
                  <span className="inline-flex items-center gap-1">
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold uppercase tracking-widest">
                      VIP
                    </span>
                    VIP-Rabatt {vipPercent ? `(${vipPercent.toFixed(vipPercent % 1 ? 1 : 0)} %)` : ''}
                  </span>
                  <span data-testid="cart-vip-discount">− {formatEUR(vipDiscountCents)}</span>
                </div>
              )}
              <div className="flex items-center justify-between text-[#3a2f1e]/70">
                <span>Netto</span>
                <span data-testid="cart-net">{formatEUR(netCents)}</span>
              </div>
              {(taxBreakdown && taxBreakdown.length > 0) ? (
                taxBreakdown.map((tb) => (
                  <div key={tb.rate} className="flex items-center justify-between text-[#3a2f1e]/70" data-testid={`cart-tax-rate-${tb.rate}`}>
                    <span>zzgl. {tb.rate}% MwSt.</span>
                    <span>{formatEUR(tb.tax_cents)}</span>
                  </div>
                ))
              ) : (
                <div className="flex items-center justify-between text-[#3a2f1e]/70" data-testid="cart-tax-total">
                  <span>zzgl. MwSt.</span>
                  <span>{formatEUR(taxCents)}</span>
                </div>
              )}
              <div className="flex items-center justify-between pt-2 border-t border-[#C9A23C]/25">
                <span className="text-[#3a2f1e]/80">Gesamt (inkl. MwSt.)</span>
                <span className="hs-serif text-xl text-[#171512]" data-testid="cart-total">
                  {formatEUR(totalCents)}
                </span>
              </div>
            </div>
            <p className="text-xs text-[#3a2f1e]/60 mb-3">Versand wird im nächsten Schritt berechnet.</p>
            <button
              type="button"
              className="hs-pill w-full py-3.5 justify-center"
              onClick={goCheckout}
              data-testid="cart-drawer-checkout"
            >
              Zur Kasse
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => setCartOpen(false)}
              className="mt-2 w-full text-center text-sm text-[#8a6d10] hover:text-[#C9A23C] py-2"
              data-testid="cart-drawer-continue"
            >
              Weiter shoppen
            </button>
          </>
        )
      }
    >
      {/* OOS-Panel: sichtbar bei OOS-Positionen im Cart. Fixiert oben mit
          Notify-Formular pro Position. */}
      {notifyOpen && oosIssues.length > 0 && (
        <div
          className="rounded-xl border-2 border-red-300 bg-gradient-to-br from-red-50 to-orange-50 p-3 mb-4"
          data-testid="cart-oos-panel"
        >
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="text-sm font-semibold text-red-900 flex items-center gap-1.5">
              <AlertTriangle className="w-4 h-4" /> {oosIssues.length} Artikel ausverkauft
            </div>
            <button
              className="text-[11px] text-stone-500 hover:text-stone-700"
              onClick={() => setNotifyOpen(false)}
              data-testid="cart-oos-close"
            >
              ×
            </button>
          </div>
          <p className="text-[11px] text-red-800/85 mb-2 leading-snug">
            Lass dich benachrichtigen, sobald diese Artikel wieder verfügbar sind:
          </p>
          <input
            type="email"
            value={notifyEmail}
            onChange={(e) => setNotifyEmail(e.target.value)}
            placeholder="deine@email.de"
            className="w-full rounded-md border border-red-200 bg-white px-3 py-1.5 text-xs mb-2 focus:border-red-500 focus:outline-none"
            data-testid="cart-oos-email"
          />
          {/* Anti-Spam Honeypot */}
          <input
            type="text"
            name="website"
            value={notifyHoney}
            onChange={(e) => setNotifyHoney(e.target.value)}
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px' }}
          />
          <div className="space-y-1.5">
            {oosIssues.map((it, i) => {
              const uid = `${it.product_id}|${it.variant?.color || ''}|${it.variant?.size || ''}`;
              const done = notifiedIds.has(uid);
              return (
                <div key={i} className="flex items-center gap-2 text-[11px]" data-testid={`cart-oos-item-${i}`}>
                  <div className="flex-1 truncate">
                    <span className="font-semibold text-red-900">{it.product_name}</span>
                    {(it.variant?.color || it.variant?.size) && (
                      <span className="text-red-800/70"> · {[it.variant?.color, it.variant?.size].filter(Boolean).join(' / ')}</span>
                    )}
                  </div>
                  <button
                    onClick={() => submitNotify(it)}
                    disabled={done || notifyBusy}
                    className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                      done ? 'bg-emerald-100 text-emerald-800 cursor-default'
                      : 'bg-red-600 text-white hover:bg-red-700 disabled:opacity-60'
                    }`}
                    data-testid={`cart-oos-notify-${i}`}
                  >
                    {done ? '✓ vorgemerkt' : 'Bescheid geben'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {items.length === 0 ? (
        <div className="text-center py-16" data-testid="cart-drawer-empty">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-white/60 flex items-center justify-center">
            <ShoppingBag className="w-8 h-8 text-[#C9A23C]" />
          </div>
          <h3 className="hs-serif text-lg mb-2 text-[#171512]">Dein Warenkorb ist leer</h3>
          <p className="text-sm text-[#3a2f1e]/70 mb-6">Entdecke unsere Kollektion und gestalte dein Design.</p>
          <button
            type="button"
            onClick={() => setCartOpen(false)}
            className="hs-pill"
          >
            Zum Katalog
          </button>
        </div>
      ) : (
        <ul className="space-y-4">
          {items.map((it) => (
            <li
              key={it.id}
              className="hs-glass p-4 flex gap-4"
              data-testid={`cart-item-${it.id}`}
            >
              {(it.design_preview_url || it.product_image) && (
                <img
                  src={it.design_preview_url || it.product_image}
                  alt={it.product_name}
                  className="w-20 h-20 rounded-xl object-cover bg-white/40"
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="hs-serif text-[#171512] leading-tight flex items-center gap-1.5 flex-wrap">
                  <span>{it.product_name}</span>
                  {(it.is_custom_made || it.exchange_notice) && (
                    <span
                      className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase tracking-wider bg-[#C9A23C] text-white"
                      title={`${cartNoticeLabel(it)} — vom Umtausch ausgeschlossen`}
                      data-testid={`cart-item-custom-made-${it.id}`}
                    >
                      {cartNoticeLabel(it)}
                    </span>
                  )}
                </div>
                <div className="text-xs text-[#3a2f1e]/70 mt-0.5">
                  {(it.variant?.color && <span>{it.variant.color}</span>) || null}
                  {it.variant?.color && it.variant?.size && <span> · </span>}
                  {it.variant?.size && <span>{it.variant.size}</span>}
                  {it.zone_name && <> · {it.zone_name}</>}
                  {(it.variant_sku || it.sku) && <> · <span className="font-mono">Art.-Nr.: {it.variant_sku || it.sku}</span></>}
                </div>
                {it.wish_text && (
                  <div
                    className="text-xs text-[#8a6d10] font-semibold mt-1"
                    data-testid={`cart-item-wish-${it.id}`}
                  >
                    Wunschtext: „{it.wish_text}"
                  </div>
                )}
                {/* Preis-Aufschlüsselung pro Position (PAngV-konform):
                    Einzelpreis inkl. MwSt · Menge · Steueranteil separat. */}
                {(() => {
                  const qty = Math.max(1, Number(it.quantity) || 1);
                  const unitGrossC = Number(it.unit_price_cents) || (Number(it.line_total_cents) / qty);
                  const rate = Number(it.tax_rate ?? 19);
                  const unitNetC = Math.round(unitGrossC / (1 + rate / 100));
                  const lineGrossC = Number(it.line_total_cents) || unitGrossC * qty;
                  const lineNetC = Math.round(lineGrossC / (1 + rate / 100));
                  const lineTaxC = lineGrossC - lineNetC;
                  return (
                    <div className="mt-2 text-[11px] leading-tight text-[#3a2f1e]/85" data-testid={`cart-item-breakdown-${it.id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span>Einzelpreis (inkl. MwSt.)</span>
                        <span className="font-semibold" data-testid={`cart-item-unit-${it.id}`}>{formatEUR(unitGrossC)}</span>
                      </div>
                      <div className="flex items-center justify-between gap-2 text-[10px] text-[#3a2f1e]/60">
                        <span>Netto {formatEUR(unitNetC)} · MwSt. {rate.toFixed(0)}%</span>
                        <span>{qty} × {formatEUR(unitGrossC)}</span>
                      </div>
                      <div className="flex items-center justify-between gap-2 text-[10px] text-[#3a2f1e]/60">
                        <span>davon MwSt. dieser Position</span>
                        <span data-testid={`cart-item-tax-${it.id}`}>{formatEUR(lineTaxC)}</span>
                      </div>
                    </div>
                  );
                })()}
                <div className="mt-2 flex items-center justify-between gap-2">
                  <div className="inline-flex items-center rounded-full bg-white/70 border border-[#C9A23C]/30 overflow-hidden">
                    <button
                      type="button"
                      onClick={() => updateCartItem(it.id, Math.max(0, (it.quantity || 1) - 1))}
                      className="w-8 h-8 hover:bg-white/80 flex items-center justify-center"
                      aria-label="Weniger"
                      data-testid={`cart-item-minus-${it.id}`}
                    >
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <span className="w-8 text-center text-sm font-semibold">{it.quantity}</span>
                    <button
                      type="button"
                      onClick={() => updateCartItem(it.id, (it.quantity || 1) + 1)}
                      className="w-8 h-8 hover:bg-white/80 flex items-center justify-center"
                      aria-label="Mehr"
                      data-testid={`cart-item-plus-${it.id}`}
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex flex-col items-end">
                      <span className="text-[10px] uppercase tracking-wider text-[#3a2f1e]/60">Zeilensumme</span>
                      <span className="hs-price text-lg" data-testid={`cart-item-total-${it.id}`}>
                        {formatEUR(it.line_total_cents)}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeCartItem(it.id)}
                      className="w-8 h-8 hover:bg-red-50 rounded-full flex items-center justify-center text-red-500 hover:text-red-700"
                      aria-label="Entfernen"
                      data-testid={`cart-item-remove-${it.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
      {items.length > 0 && <CartCrossSell cartItems={items} />}
    </DrawerShell>
    <HCaptchaModal
      open={captchaOpen}
      message={captchaMsg}
      onSolve={(token) => { if (pendingItem) submitNotify(pendingItem, token); }}
      onClose={() => { setCaptchaOpen(false); setPendingItem(null); }}
    />
    </>
  );
}

// ------------------------------------------------------------------
// WISHLIST DRAWER
// ------------------------------------------------------------------
export function WishlistDrawer() {
  const { wishlist, wishlistOpen, setWishlistOpen, removeWishlistItem, wishlistCount } = useCart();
  const navigate = useNavigate();
  const items = wishlist?.items || [];

  const goQuote = () => {
    setWishlistOpen(false);
    navigate('/merkzettel/angebot');
  };

  return (
    <DrawerShell
      open={wishlistOpen}
      onClose={() => setWishlistOpen(false)}
      title={`Merkzettel (${wishlistCount})`}
      testId="wishlist-drawer"
      footer={
        items.length > 0 && (
          <>
            <button
              type="button"
              className="hs-pill w-full py-3.5 justify-center"
              onClick={goQuote}
              data-testid="wishlist-drawer-quote"
            >
              <FileText className="w-4 h-4" />
              Angebot anfordern
            </button>
            <p className="text-xs text-[#3a2f1e]/60 mt-3 text-center">
              Unverbindliches B2B-Angebot – wir melden uns innerhalb von 24 Stunden.
            </p>
          </>
        )
      }
    >
      {items.length === 0 ? (
        <div className="text-center py-16" data-testid="wishlist-drawer-empty">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-white/60 flex items-center justify-center">
            <Heart className="w-8 h-8 text-[#C9A23C]" />
          </div>
          <h3 className="hs-serif text-lg mb-2 text-[#171512]">Dein Merkzettel ist leer</h3>
          <p className="text-sm text-[#3a2f1e]/70 mb-6">
            Speichere Produkte, um daraus später ein individuelles Angebot anzufordern.
          </p>
        </div>
      ) : (
        <ul className="space-y-4">
          {items.map((it) => (
            <li
              key={it.id}
              className="hs-glass p-4 flex gap-4"
              data-testid={`wishlist-item-${it.id}`}
            >
              {(it.design_preview_url || it.product_image) && (
                <Link to={`/shop/${it.product_slug}`} onClick={() => setWishlistOpen(false)} className="shrink-0">
                  <img
                    src={it.design_preview_url || it.product_image}
                    alt={it.product_name}
                    className="w-20 h-20 rounded-xl object-cover bg-white/40"
                  />
                </Link>
              )}
              <div className="flex-1 min-w-0">
                <Link to={`/shop/${it.product_slug}`} onClick={() => setWishlistOpen(false)} className="hs-serif text-[#171512] leading-tight hover:text-[#8a6d10]">
                  {it.product_name}
                </Link>
                <div className="text-xs text-[#3a2f1e]/70 mt-0.5">
                  {it.variant?.color && <span>{it.variant.color}</span>}
                  {it.variant?.color && it.variant?.size && <span> · </span>}
                  {it.variant?.size && <span>{it.variant.size}</span>}
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-xs text-[#3a2f1e]/60">Menge: {it.quantity || 1}</span>
                  <button
                    type="button"
                    onClick={() => removeWishlistItem(it.id)}
                    className="text-red-500 hover:text-red-700 text-xs inline-flex items-center gap-1"
                    data-testid={`wishlist-item-remove-${it.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Entfernen
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </DrawerShell>
  );
}

// ------------------------------------------------------------------
// Nav Buttons (Warenkorb + Merkzettel Icons)
// ------------------------------------------------------------------
export function CartWishlistIcons({ small = false }) {
  const { cartCount, wishlistCount, setCartOpen, setWishlistOpen } = useCart();
  const sizeClass = small ? 'w-9 h-9' : 'w-10 h-10';
  return (
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        onClick={() => setWishlistOpen(true)}
        className={`relative ${sizeClass} inline-flex items-center justify-center rounded-full hover:bg-white/60 text-[#171512]`}
        aria-label="Merkzettel öffnen"
        data-testid="nav-wishlist-btn"
      >
        <Heart className="w-4 h-4" />
        {wishlistCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-[#C9A23C] text-white text-[10px] font-bold flex items-center justify-center" data-testid="nav-wishlist-count">
            {wishlistCount}
          </span>
        )}
      </button>
      <button
        type="button"
        onClick={() => setCartOpen(true)}
        className={`relative ${sizeClass} inline-flex items-center justify-center rounded-full hover:bg-white/60 text-[#171512]`}
        aria-label="Warenkorb öffnen"
        data-testid="nav-cart-btn"
      >
        <ShoppingBag className="w-4 h-4" />
        {cartCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-[#C9A23C] text-white text-[10px] font-bold flex items-center justify-center" data-testid="nav-cart-count">
            {cartCount}
          </span>
        )}
      </button>
    </div>
  );
}
